import React, { createContext, useContext, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LogIn, UserPlus, X } from "lucide-react";
import { useI18n } from "@/lib/i18n";

/**
 * Global "guest gate" dialog.
 * openGate({ message }) opens a small modal over the current screen with:
 * - Accedi
 * - Registrati
 * - Chiudi (X)
 * It does NOT navigate away until the user picks. Underlying screen stays intact.
 */
const GuestGateCtx = createContext({ openGate: () => {}, close: () => {} });

export function GuestGateProvider({ children }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const nav = useNavigate();
  const loc = useLocation();

  const openGate = useCallback((opts = {}) => {
    setMessage(opts.message || t("guest_gate_action"));
    setOpen(true);
  }, [t]);

  const close = useCallback(() => setOpen(false), []);

  const goAuth = (mode) => {
    setOpen(false);
    const next = encodeURIComponent(loc.pathname + loc.search);
    nav(`/auth?mode=${mode}&next=${next}`);
  };

  return (
    <GuestGateCtx.Provider value={{ openGate, close }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="guest-gate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl" data-testid="guest-gate-title">{t("guest_gate_title")}</DialogTitle>
            <DialogDescription className="text-slate-600" data-testid="guest-gate-message">
              {message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
            <Button variant="outline" onClick={() => goAuth("login")} data-testid="guest-gate-login" className="rounded-full hover:bg-slate-900 hover:text-white">
              <LogIn size={14} className="mr-1.5" /> {t("guest_gate_login")}
            </Button>
            <Button onClick={() => goAuth("signup")} data-testid="guest-gate-signup" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
              <UserPlus size={14} className="mr-1.5" /> {t("guest_gate_signup")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </GuestGateCtx.Provider>
  );
}

export function useGuestGate() {
  return useContext(GuestGateCtx);
}
